import type {
  CreateReportRequest,
  CreateReportResponse,
  DownloadPDFResponse,
  EditDraftRequest,
  GenerateReportJobResponse,
  GetReportJobResponse,
  GetReportResponse,
  ListReportsParams,
  ListReportsResponse,
  PollStatusResponse,
  RefreshMartResponse,
  StandardResponse,
} from "@/pages/Reports/types";

import { useSettingsStore } from "../../stores/settingsStore";
import axiosClient from "../axiosClient";

// X-T-Id is sent explicitly so the gateway can inject the correct tenant
// context for platform/super-admin users who have no tenant claim in their JWT.
const tIdHeader = (agencyId?: string): Record<string, string> =>
  agencyId ? { "X-T-Id": agencyId } : {};

export interface ReportEvent {
  id: number;
  report_id: string;
  event_type: string;
  actor_id: string | null;
  from_status: string | null;
  to_status: string | null;
  created_at: string;
}

/**
 * Report Service
 * Handles report generation, editing, submission, and fetching
 */
export const reportService = {
  /**
   * Create a new report (initiates report generation)
   * Returns 202 with report_id and PENDING status
   * agencyId is passed as X-T-Id for tenant DB routing
   */
  createReport: async (
    request: CreateReportRequest,
    agencyId?: string,
  ): Promise<CreateReportResponse> => {
    const { data } = await axiosClient.post<CreateReportResponse>(
      "/reports",
      { ...request, report_type: request.report_type || "summary" },
      { headers: tIdHeader(agencyId) },
    );
    return data;
  },

  /**
   * Get report by ID (full report data)
   * Returns 200 with full report data when status is DRAFT/PUBLISHED
   * agencyId is passed as X-T-Id for tenant DB routing
   */
  getReportById: async (
    reportId: string,
    agencyId?: string,
  ): Promise<GetReportResponse> => {
    const { data } = await axiosClient.get<GetReportResponse>(
      `/reports/${reportId}`,
      { headers: tIdHeader(agencyId) },
    );
    return data;
  },

  /**
   * Poll report status (lightweight endpoint)
   * Returns only status, draft_version, error_message, and updated_at
   * agencyId is passed as X-T-Id for tenant DB routing
   */
  pollStatus: async (
    reportId: string,
    agencyId?: string,
  ): Promise<PollStatusResponse> => {
    const { data } = await axiosClient.get<PollStatusResponse>(
      `/reports/${reportId}/status`,
      { headers: tIdHeader(agencyId) },
    );
    return data;
  },

  /**
   * Edit draft report (partial update)
   * Only available when status is DRAFT and editable is true
   * agencyId is passed as X-T-Id for tenant DB routing
   */
  editDraft: async (
    reportId: string,
    updates: EditDraftRequest,
    agencyId?: string,
  ): Promise<GetReportResponse> => {
    const { data } = await axiosClient.patch<GetReportResponse>(
      `/reports/${reportId}/draft`,
      updates,
      { headers: tIdHeader(agencyId) },
    );
    return data;
  },

  /**
   * Submit report for PDF generation and publishing
   * Changes status to SUBMITTED and locks editing
   * agencyId is passed as X-T-Id for tenant DB routing
   */
  submitReport: async (
    reportId: string,
    agencyId?: string,
  ): Promise<StandardResponse> => {
    const { data } = await axiosClient.post<StandardResponse>(
      `/reports/${reportId}/submit`,
      {},
      { headers: tIdHeader(agencyId) },
    );
    return data;
  },

  /**
   * Retry failed report
   * Resets status to PENDING and re-launches background jobs
   * agencyId is passed as X-T-Id for tenant DB routing
   */
  retryReport: async (
    reportId: string,
    agencyId?: string,
  ): Promise<StandardResponse> => {
    const { data } = await axiosClient.post<StandardResponse>(
      `/reports/${reportId}/retry`,
      {},
      { headers: tIdHeader(agencyId) },
    );
    return data;
  },

  /**
   * Get PDF download URL (signed URL, expires in 15 minutes)
   * Only available when pdf_available is true
   * agencyId is passed as X-T-Id for tenant DB routing
   */
  downloadPDF: async (
    reportId: string,
    agencyId?: string,
  ): Promise<DownloadPDFResponse> => {
    const { data } = await axiosClient.get<DownloadPDFResponse>(
      `/reports/${reportId}/download`,
      { headers: tIdHeader(agencyId) },
    );
    return data;
  },

  /**
   * List reports with pagination and filters
   * Agency users can filter by status and client_id
   * Customer users only see PUBLISHED reports for their client (auto-scoped)
   * agencyId is passed as X-T-Id for tenant DB routing
   */
  listReports: async (
    params?: ListReportsParams,
    agencyId?: string,
  ): Promise<ListReportsResponse> => {
    const { data } = await axiosClient.get<ListReportsResponse>("/reports", {
      params,
      headers: tIdHeader(agencyId),
    });
    return data;
  },

  /**
   * Delete a report
   * Requires reports.delete permission
   */
  delete: async (
    reportId: string,
    agencyId?: string,
  ): Promise<StandardResponse> => {
    const { data } = await axiosClient.delete<StandardResponse>(
      `/reports/${reportId}`,
      { headers: tIdHeader(agencyId) },
    );
    return data;
  },

  /**
   * Get status event history for a report (chronological order).
   * GET /reports/:id/events
   */
  getEvents: async (
    reportId: string,
    agencyId?: string,
  ): Promise<ReportEvent[]> => {
    const { data } = await axiosClient.get<{
      success: boolean;
      responseData: ReportEvent[];
    }>(`/reports/${reportId}/events`, { headers: tIdHeader(agencyId) });
    return data.responseData;
  },

  // ---------------------------------------------------------------------------
  // PDF generation job (generate → poll → download)
  // ---------------------------------------------------------------------------

  /**
   * Trigger PDF generation for a submitted report.
   * Uses sync job_type — response contains final status immediately.
   * POST /reporting/reports/generate
   */
  generateReportPDF: async (
    payload: unknown,
  ): Promise<GenerateReportJobResponse> => {
    const language = useSettingsStore.getState().language;
    const reportType =
      language === "en" ? "WEB_ANALYTICS_EN" : "WEB_ANALYTICS_JP";
    const { data } = await axiosClient.post<GenerateReportJobResponse>(
      "/reporting/reports/generate",
      { job_type: "sync", report_type: reportType, payload },
    );
    return data;
  },

  /**
   * Poll the status of a report generation job.
   * GET /reporting/reports/:jobId
   */
  getReportJobStatus: async (jobId: number): Promise<GetReportJobResponse> => {
    const { data } = await axiosClient.get<GetReportJobResponse>(
      `/reporting/reports/${jobId}`,
    );
    return data;
  },

  /**
   * Download the generated PDF file for a completed job.
   * Only callable when job status === "SUCCESS".
   * GET /reporting/reports/:jobId/file — returns binary PDF.
   */
  downloadReportFile: async (jobId: number): Promise<Blob> => {
    const { data } = await axiosClient.get(`/reporting/reports/${jobId}/file`, {
      responseType: "blob",
    });
    return data as Blob;
  },

  /**
   * Trigger a BigQuery data mart refresh.
   * POST /reports/refresh-mart
   * Requires agency or platform scope. Returns 202 on success.
   */
  refreshMart: async (): Promise<RefreshMartResponse> => {
    const { data } = await axiosClient.post<RefreshMartResponse>(
      "/reports/refresh-mart",
    );
    return data;
  },

  /**
   * Platform-admin: aggregate report counts across all tenant databases.
   * No X-T-Id required. GET /reports/platform
   */
  getPlatformStats: async (): Promise<{
    total_reports: number;
    published_reports: number;
    tenant_count: number;
    by_agency: Array<{
      agency_id: string;
      total_reports: number;
      published_reports: number;
    }>;
  }> => {
    const { data } = await axiosClient.get<{
      success: boolean;
      responseData: {
        total_reports: number;
        published_reports: number;
        tenant_count: number;
        by_agency: Array<{
          agency_id: string;
          total_reports: number;
          published_reports: number;
        }>;
      };
    }>("/reports/platform");
    return data.responseData;
  },

  /**
   * Platform-admin: aggregate conversion metrics from BigQuery across all tenants.
   * No X-T-Id required. GET /reports/platform/conversions
   */
  getPlatformConversions: async (
    days = 30,
  ): Promise<{
    by_agency: Array<{
      agency_id: string;
      total_conversions: number;
      total_clicks: number;
      total_impressions: number;
      total_cost: number;
    }>;
    totals: {
      total_conversions: number;
      total_clicks: number;
      total_impressions: number;
      total_cost: number;
    };
    days: number;
    sources: string[];
  }> => {
    const { data } = await axiosClient.get<{
      success: boolean;
      responseData: {
        by_agency: Array<{
          agency_id: string;
          total_conversions: number;
          total_clicks: number;
          total_impressions: number;
          total_cost: number;
        }>;
        totals: {
          total_conversions: number;
          total_clicks: number;
          total_impressions: number;
          total_cost: number;
        };
        days: number;
        sources: string[];
      };
    }>(`/reports/platform/conversions?days=${days}`);
    return data.responseData;
  },

  /**
   * Platform-admin: report counts grouped by month and by status across all tenants.
   * No X-T-Id required. GET /reports/analytics/stats
   */
  getReportAnalyticsStats: async (
    months = 6,
    agencyId?: string,
  ): Promise<{
    by_status: Record<string, number>;
    monthly_trend: Array<{ month: string; total: number; published: number }>;
    months: number;
  }> => {
    const params = new URLSearchParams({ months: String(months) });
    if (agencyId) params.set("agency_id", agencyId);
    const { data } = await axiosClient.get<{
      success: boolean;
      responseData: {
        by_status: Record<string, number>;
        monthly_trend: Array<{
          month: string;
          total: number;
          published: number;
        }>;
        months: number;
      };
    }>(`/reports/analytics/stats?${params.toString()}`);
    return data.responseData;
  },

  /**
   * Platform-admin: monthly report counts per customer (org_id).
   * No X-T-Id required. GET /reports/analytics/monthly-matrix
   */
  getMonthlyMatrix: async (
    months = 6,
    agencyId?: string,
    orgId?: string,
  ): Promise<
    Array<{
      month: string;
      org_id: string;
      report_count: number;
      published_count: number;
      failed_count: number;
    }>
  > => {
    const params = new URLSearchParams({ months: String(months) });
    if (agencyId) params.set("agency_id", agencyId);
    if (orgId) params.set("org_id", orgId);
    const { data } = await axiosClient.get<{
      success: boolean;
      responseData: Array<{
        month: string;
        org_id: string;
        report_count: number;
        published_count: number;
        failed_count: number;
      }>;
    }>(`/reports/analytics/monthly-matrix?${params.toString()}`);
    return data.responseData;
  },

  /**
   * Platform-admin: acquisition, engagement and conversion KPIs for a single
   * customer, sourced from BigQuery mart tables.
   * No X-T-Id required. GET /reports/analytics/customer-kpis
   */
  getCustomerKpis: async (
    orgId: string,
    agencyId: string,
    days = 30,
  ): Promise<{
    acquisition: {
      sessions: number;
      new_users: number;
      new_user_rate: number;
      sessions_mom: number;
      new_users_mom: number;
      new_user_rate_mom: number;
    };
    engagement: {
      pageviews: number;
      pages_per_session: number;
      engagement_rate: number;
      pageviews_mom: number;
      pages_per_session_mom: number;
      engagement_rate_mom: number;
    };
    conversion: {
      conversions: number;
      cvr: number;
      revenue: number;
      conversions_mom: number;
      cvr_mom: number;
      revenue_mom: number;
    };
  }> => {
    const { data } = await axiosClient.get<{
      success: boolean;
      responseData: {
        acquisition: {
          sessions: number;
          new_users: number;
          new_user_rate: number;
          sessions_mom: number;
          new_users_mom: number;
          new_user_rate_mom: number;
        };
        engagement: {
          pageviews: number;
          pages_per_session: number;
          engagement_rate: number;
          pageviews_mom: number;
          pages_per_session_mom: number;
          engagement_rate_mom: number;
        };
        conversion: {
          conversions: number;
          cvr: number;
          revenue: number;
          conversions_mom: number;
          cvr_mom: number;
          revenue_mom: number;
        };
      };
    }>(
      `/reports/analytics/customer-kpis?org_id=${orgId}&agency_id=${agencyId}&days=${days}`,
    );
    return data.responseData;
  },
};
